export interface PlatformSettings {
  platformName: string;
  defaultLanguage: string;
  timezone: string;
  sessionTimeout: number;
  maintenanceMode: boolean;
}

export interface FrequencySettings {
  initialFrequency: number;
  finalFrequency: number;
  increment: number;
  autoGeneration: boolean;
  autoApproval: boolean;
  requireDocuments: boolean;
  reservedFrequencies: string;
}

export interface PreferredFrequencySettings {
  enabled: boolean;
  requiresApproval: boolean;
  price: number;
  generateInvoiceImmediately: boolean;
  applyAfterPayment: boolean;
}

export interface PlanLimits {
  storageGB: number;
  livesPerWeek: number;
  cabinaMessagesPerDay: number;
  podcastHours: number;
  photoUploadMB: number;
  videoUploadMB: number;
  maxRadiosPerUser: number;
}

export interface PlanSettings {
  freeLimits: PlanLimits;
  proPrice: number;
  premiumPrice: number;
  trialDays: number;
}

export interface AdSettings {
  globalAdsEnabled: boolean;
  adFrequency: 'low' | 'normal' | 'high';
  maxAdsPerPage: number;
  allowedFormats: string[];
  autoApproveAds: boolean;
}

export interface StorageSettings {
  globalLimitTB: number;
  retentionDays: number;
  autoCleanup: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  maxFileSizeMB: number;
}

export interface EmailSettings {
  smtpHost: string;
  smtpPort: number;
  senderEmail: string;
  senderName: string;
  welcomeEmailEnabled: boolean;
  billingEmailEnabled: boolean;
  weeklyDigestEnabled: boolean;
}

export interface ModerationSettings {
  globalModeration: boolean;
  autoBlockEnabled: boolean;
  profanityFilterEnabled: boolean;
  linkFilterEnabled: boolean;
  maxReportsBeforeAutoBan: number;
  bannedWords: string;
}

export interface AppSettings {
  minVersionAndroid: string;
  minVersionIOS: string;
  forceUpdate: boolean;
  maintenanceMode: boolean;
  maintenanceMessage: string;
}

// --- NUEVAS interfaces faltantes ---

export interface LicenseSettings {
  enabled: boolean;
  defaultDurationMonths: number;
  autoRenewEnabled: boolean;
  gracePeriodDays: number;
  renewalReminderDays: number;
  requireDocumentation: boolean;
  allowEarlyRenewal: boolean;
}

export interface ContractsSettings {
  autoGenerateOnApproval: boolean;
  signatureExpirationDays: number;
  requireDigitalSignature: boolean;
  allowCustomClauses: boolean;
  defaultTemplate: string;
  reminderBeforeExpiryDays: number;
}

export interface BillingSettings {
  invoicePrefix: string;
  defaultCurrency: string;
  taxRate: number;
  taxId: string;
  paymentTermsDays: number;
  lateFeePercentage: number;
  generateInvoiceAutomatically: boolean;
  allowPartialPayments: boolean;
}

export interface PaymentProcessorsSettings {
  stripeEnabled: boolean;
  paypalEnabled: boolean;
  bankTransferEnabled: boolean;
  cryptoEnabled: boolean;
  defaultProcessor: 'stripe' | 'paypal' | 'bank_transfer';
  testMode: boolean;
}

export interface PremiumDomainSettings {
  enabled: boolean;
  priceUSD: number;
  includedInPlan: 'premium' | 'none';
  autoAssign: boolean;
  maxSubdomains: number;
  allowCustomDNS: boolean;
}

export interface PermanentQRSettings {
  enabled: boolean;
  includedInPlans: string[];
  allowCustomDesign: boolean;
  allowCustomLogo: boolean;
  trackScans: boolean;
  defaultSize: number;
}

export interface LivesSettings {
  maxDurationMinutes: number;
  autoRecord: boolean;
  maxQuality: '720p' | '1080p' | '4K';
  allowExternalRTMP: boolean;
  concurrentViewersLimit: number;
  chatEnabled: boolean;
  replayEnabled: boolean;
}

export interface SupportSettings {
  autoAssignTickets: boolean;
  defaultPriority: 'low' | 'normal' | 'high' | 'urgent';
  responseSLAMinutes: number;
  resolutionSLADays: number;
  allowAttachments: boolean;
  maxAttachmentMB: number;
  supportEmail: string;
}

export interface SupportTranslationSettings {
  enabled: boolean;
  sourceLanguage: string;
  targetLanguages: string[];
  autoDetectEnabled: boolean;
  translateIncoming: boolean;
  translateOutgoing: boolean;
}

export interface NotificationsSettings {
  pushEnabled: boolean;
  emailDigestEnabled: boolean;
  inAppEnabled: boolean;
  rateLimitPerMinute: number;
  maxPerUserPerDay: number;
  quietHoursStart: string;
  quietHoursEnd: string;
}

export interface GlobalSettings {
  platform: PlatformSettings;
  frequencies: FrequencySettings;
  preferredFrequency: PreferredFrequencySettings;
  plans: PlanSettings;
  ads: AdSettings;
  storage: StorageSettings;
  email: EmailSettings;
  moderation: ModerationSettings;
  app: AppSettings;
  license: LicenseSettings;
  contracts: ContractsSettings;
  billing: BillingSettings;
  paymentProcessors: PaymentProcessorsSettings;
  premiumDomain: PremiumDomainSettings;
  permanentQR: PermanentQRSettings;
  lives: LivesSettings;
  support: SupportSettings;
  supportTranslation: SupportTranslationSettings;
  notifications: NotificationsSettings;
}

export const initialSettings: GlobalSettings = {
  platform: {
    platformName: 'RadioFrecuenciaWeb',
    defaultLanguage: 'es',
    timezone: 'America/Bogota',
    sessionTimeout: 120,
    maintenanceMode: false,
  },
  frequencies: {
    initialFrequency: 87.00,
    finalFrequency: 200.00,
    increment: 0.01,
    autoGeneration: true,
    autoApproval: false,
    requireDocuments: true,
    reservedFrequencies: '87.50, 87.70, 88.10, 88.30',
  },
  preferredFrequency: {
    enabled: true,
    requiresApproval: true,
    price: 49,
    generateInvoiceImmediately: true,
    applyAfterPayment: true,
  },
  plans: {
    freeLimits: {
      storageGB: 5,
      livesPerWeek: 1,
      cabinaMessagesPerDay: 50,
      podcastHours: 10,
      photoUploadMB: 5,
      videoUploadMB: 50,
      maxRadiosPerUser: 1,
    },
    proPrice: 19,
    premiumPrice: 49,
    trialDays: 14,
  },
  ads: {
    globalAdsEnabled: true,
    adFrequency: 'normal',
    maxAdsPerPage: 3,
    allowedFormats: ['banner', 'sidebar', 'in-stream'],
    autoApproveAds: false,
  },
  storage: {
    globalLimitTB: 5,
    retentionDays: 90,
    autoCleanup: true,
    backupFrequency: 'daily',
    maxFileSizeMB: 500,
  },
  email: {
    smtpHost: 'smtp.radiofrecuenciaweb.com',
    smtpPort: 587,
    senderEmail: 'no-reply@radiofrecuenciaweb.com',
    senderName: 'RadioFrecuenciaWeb',
    welcomeEmailEnabled: true,
    billingEmailEnabled: true,
    weeklyDigestEnabled: false,
  },
  moderation: {
    globalModeration: true,
    autoBlockEnabled: true,
    profanityFilterEnabled: true,
    linkFilterEnabled: true,
    maxReportsBeforeAutoBan: 5,
    bannedWords: 'spam, casino, apuestas, viagra, pills, forex, crypto, bitcoin',
  },
  app: {
    minVersionAndroid: '2.1.0',
    minVersionIOS: '2.1.0',
    forceUpdate: false,
    maintenanceMode: false,
    maintenanceMessage: 'Estamos realizando tareas de mantenimiento. Volveremos en breve.',
  },
  license: {
    enabled: true,
    defaultDurationMonths: 12,
    autoRenewEnabled: true,
    gracePeriodDays: 15,
    renewalReminderDays: 30,
    requireDocumentation: true,
    allowEarlyRenewal: true,
  },
  contracts: {
    autoGenerateOnApproval: true,
    signatureExpirationDays: 7,
    requireDigitalSignature: true,
    allowCustomClauses: false,
    defaultTemplate: 'standard_es',
    reminderBeforeExpiryDays: 3,
  },
  billing: {
    invoicePrefix: 'RFW-',
    defaultCurrency: 'USD',
    taxRate: 0,
    taxId: 'N/A',
    paymentTermsDays: 15,
    lateFeePercentage: 5,
    generateInvoiceAutomatically: true,
    allowPartialPayments: false,
  },
  paymentProcessors: {
    stripeEnabled: true,
    paypalEnabled: true,
    bankTransferEnabled: false,
    cryptoEnabled: false,
    defaultProcessor: 'stripe',
    testMode: true,
  },
  premiumDomain: {
    enabled: true,
    priceUSD: 29,
    includedInPlan: 'premium',
    autoAssign: false,
    maxSubdomains: 3,
    allowCustomDNS: true,
  },
  permanentQR: {
    enabled: true,
    includedInPlans: ['pro', 'premium'],
    allowCustomDesign: false,
    allowCustomLogo: true,
    trackScans: true,
    defaultSize: 300,
  },
  lives: {
    maxDurationMinutes: 180,
    autoRecord: true,
    maxQuality: '1080p',
    allowExternalRTMP: true,
    concurrentViewersLimit: 500,
    chatEnabled: true,
    replayEnabled: true,
  },
  support: {
    autoAssignTickets: true,
    defaultPriority: 'normal',
    responseSLAMinutes: 60,
    resolutionSLADays: 3,
    allowAttachments: true,
    maxAttachmentMB: 10,
    supportEmail: 'soporte@radiofrecuenciaweb.com',
  },
  supportTranslation: {
    enabled: false,
    sourceLanguage: 'es',
    targetLanguages: ['en', 'pt'],
    autoDetectEnabled: true,
    translateIncoming: true,
    translateOutgoing: false,
  },
  notifications: {
    pushEnabled: true,
    emailDigestEnabled: false,
    inAppEnabled: true,
    rateLimitPerMinute: 10,
    maxPerUserPerDay: 100,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
  },
};

export const sectionMeta: Record<string, { icon: string; label: string; description: string }> = {
  platform: { icon: 'ri-computer-line', label: 'Plataforma', description: 'Configuración general del sistema' },
  frequencies: { icon: 'ri-radar-line', label: 'Frecuencias RFW', description: 'Rangos, auto-generación y aprobación' },
  preferredFrequency: { icon: 'ri-vip-diamond-line', label: 'Frecuencia Preferida', description: 'Solicitudes, precio y facturación de frecuencias preferidas' },
  plans: { icon: 'ri-stack-line', label: 'Planes', description: 'Límites de planes y precios base' },
  ads: { icon: 'ri-advertisement-line', label: 'Publicidad', description: 'Control global de anuncios' },
  storage: { icon: 'ri-hard-drive-3-line', label: 'Storage', description: 'Límites, backups y retención' },
  email: { icon: 'ri-mail-settings-line', label: 'Email', description: 'Servidor SMTP y notificaciones' },
  moderation: { icon: 'ri-shield-check-line', label: 'Moderación', description: 'Filtros y auto-bloqueo global' },
  app: { icon: 'ri-smartphone-line', label: 'App', description: 'Versiones mínimas y mantenimiento' },
  license: { icon: 'ri-key-2-line', label: 'Licencia Anual', description: 'Duración, renovación automática y período de gracia' },
  contracts: { icon: 'ri-file-text-line', label: 'Contratos', description: 'Generación automática, firma digital y vencimiento' },
  billing: { icon: 'ri-bank-card-line', label: 'Facturación', description: 'Prefijo, moneda, impuestos y plazos de pago' },
  paymentProcessors: { icon: 'ri-money-dollar-circle-line', label: 'Procesadores de Pago', description: 'Stripe, PayPal, transferencias y modo test' },
  premiumDomain: { icon: 'ri-globe-line', label: 'Dominio Premium', description: 'Precio, planes incluidos y subdominios' },
  permanentQR: { icon: 'ri-qr-code-line', label: 'QR Permanente', description: 'Planes, diseño personalizado y tracking' },
  lives: { icon: 'ri-live-line', label: 'Lives', description: 'Duración, calidad, RTMP externo y grabación' },
  support: { icon: 'ri-customer-service-2-line', label: 'Soporte', description: 'Asignación automática, SLAs y adjuntos' },
  supportTranslation: { icon: 'ri-translate-2', label: 'Traducción Soporte', description: 'Idiomas origen/destino y detección automática' },
  notifications: { icon: 'ri-notification-3-line', label: 'Notificaciones', description: 'Push, email, rate limiting y horas silenciosas' },
};

export const languageOptions = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português' },
  { value: 'fr', label: 'Français' },
];

export const timezoneOptions = [
  { value: 'America/Bogota', label: 'Bogotá (GMT-5)' },
  { value: 'America/Mexico_City', label: 'Ciudad de México (GMT-6)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (GMT-3)' },
  { value: 'America/Santiago', label: 'Santiago (GMT-4)' },
  { value: 'America/Lima', label: 'Lima (GMT-5)' },
  { value: 'Europe/Madrid', label: 'Madrid (GMT+1)' },
  { value: 'America/New_York', label: 'New York (GMT-5)' },
];

export const frequencyOptions = [
  { value: 'low', label: 'Baja' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' },
];

export const backupOptions = [
  { value: 'daily', label: 'Diario' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
];

export const currencyOptions = [
  { value: 'USD', label: 'USD — Dólar estadounidense' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'COP', label: 'COP — Peso colombiano' },
  { value: 'MXN', label: 'MXN — Peso mexicano' },
  { value: 'ARS', label: 'ARS — Peso argentino' },
  { value: 'CLP', label: 'CLP — Peso chileno' },
  { value: 'PEN', label: 'PEN — Sol peruano' },
];

export const qualityOptions = [
  { value: '720p', label: '720p HD' },
  { value: '1080p', label: '1080p Full HD' },
  { value: '4K', label: '4K Ultra HD' },
];

export const priorityOptions = [
  { value: 'low', label: 'Baja' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export const planOptions = [
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
];

export const templateOptions = [
  { value: 'standard_es', label: 'Estándar (ES)' },
  { value: 'standard_en', label: 'Estándar (EN)' },
  { value: 'simplified_es', label: 'Simplificado (ES)' },
  { value: 'legal_full_es', label: 'Legal completo (ES)' },
];